import React from 'react';
import moment from 'moment-timezone';
import { Icon } from '@blueprintjs/core';
import { currentTime } from 'helpers/time';
import { priorityMapping } from 'helpers/priorityTag';
import { jobStatusOptions } from 'helpers/jobStatusOptions';
import { serviceTypeColors } from 'helpers/serviceTypeColor';
import * as DataList from 'components/DataList';

// Job-specific pieces for the Jobs and Assignments lists, built on the shared
// DataList (components/DataList). Job-only styles: styles/schedule-list.scss.

// Column and card layouts, shared by the lists and their loading skeletons.
export const MIN_TABLE_WIDTH = 1260;

const COLUMN = {
  number: { area: 'number', label: 'Job #', width: '148px', bone: 'short' },
  job: { area: 'job', label: 'Job', width: 'minmax(170px, 1.3fr)' },
  when: { area: 'when', label: 'Scheduled', width: '132px', bone: 'short', icon: 'time' },
  team: { area: 'team', label: 'Technicians', width: 'minmax(160px, 1fr)', bone: 'person', icon: 'people' },
  client: { area: 'client', label: 'Client', width: 'minmax(190px, 1.4fr)', icon: 'map-marker' },
  created: { area: 'created', label: 'Created', width: '124px', bone: 'short' },
  billing: { area: 'billing', label: 'Billing', width: '104px', bone: 'pill' },
  priority: { area: 'priority', label: 'Priority', width: '124px', bone: 'pill' },
  deleted: { area: 'deleted', label: 'Deleted', width: '96px', bone: 'short' },
  actions: { area: 'actions', label: '', width: '32px', bone: 'none' },
};

export const jobColumns = (deleted) => [
  COLUMN.number, COLUMN.job, COLUMN.when, COLUMN.team, COLUMN.client,
  COLUMN.created, COLUMN.billing, COLUMN.priority,
  ...(deleted ? [COLUMN.deleted] : []),
  COLUMN.actions,
];

export const jobCards = (deleted) => ({
  columns: 'minmax(0, 1fr) auto auto',
  areas: [
    'number priority actions',
    'job job job',
    'when when when',
    'team team team',
    'client client client',
    ...(deleted ? ['deleted deleted deleted'] : []),
    'created billing billing',
  ],
  footer: ['created', 'billing'],
});

export const ASSIGNMENT_COLUMNS = [
  COLUMN.number,
  { ...COLUMN.job, width: 'minmax(200px, 1.3fr)' },
  COLUMN.when,
  { ...COLUMN.team, label: 'Technician', width: 'minmax(180px, 1fr)' },
  { ...COLUMN.client, width: 'minmax(220px, 1.4fr)' },
  COLUMN.actions,
];

export const ASSIGNMENT_CARDS = {
  columns: 'minmax(0, 1fr) auto',
  areas: ['number actions', 'job job', 'when when', 'team team', 'client client'],
};

const STATUSES = jobStatusOptions();
const UNASSIGNED = STATUSES.find((status) => status.value === 'unassigned');

export const statusFor = (value) =>
  STATUSES.find((status) => status.value === value) || UNASSIGNED;

const sameDay = (a, b) => a.isSame(b, 'day');

const dayLabel = (time) =>
  time.format(time.isSame(currentTime(), 'year') ? 'ddd, MMM D' : 'ddd, MMM D, YYYY');

const timeRange = (start, end) => {
  if (!end) return start.format('h:mm A');
  if (start.format('A') === end.format('A')) {
    return `${start.format('h:mm')} – ${end.format('h:mm A')}`;
  }
  return `${start.format('h:mm A')} – ${end.format('h:mm A')}`;
};

export const accentFor = (serviceTypeColor) => serviceTypeColors(serviceTypeColor).background;

export const When = ({ start, end }) => {
  if (!start) return <span className="dl-muted">Not scheduled</span>;
  const startTime = currentTime(start);
  const endTime = end ? currentTime(end) : null;
  const multiDay = endTime && !sameDay(startTime, endTime);
  return (
    <div className="schedule-when">
      <div className="schedule-when__day">{dayLabel(startTime)}</div>
      {multiDay ? (
        <div className="schedule-when__time">
          {startTime.format('h:mm A')} until
          <br />
          {dayLabel(endTime)}, {endTime.format('h:mm A')}
        </div>
      ) : (
        <div className="schedule-when__time">{timeRange(startTime, endTime)}</div>
      )}
    </div>
  );
};

// Job number (the row link) over the service type.
export const JobNumber = ({ number, link, title, serviceType }) => (
  <div className="schedule-number">
    <DataList.RowLink href={link} label={`Job ${number}: ${title || ''}`}>
      #{number}
    </DataList.RowLink>
    {serviceType && serviceType.name && (
      <div className="schedule-number__type">
        <span
          className="schedule-number__dot"
          style={{ backgroundColor: serviceTypeColors(serviceType.color).background }}
        />
        <span className="dl-truncate">{serviceType.name}</span>
      </div>
    )}
  </div>
);

export const JobTitle = ({ title, details = [] }) => {
  const shown = details.filter(Boolean);
  return (
    <div className="schedule-job">
      <div className="schedule-job__title" title={title}>{title || 'Untitled job'}</div>
      {shown.length > 0 && (
        <div className="dl-muted schedule-job__details" title={shown.join(', ')}>
          {shown.map((detail) => <span key={detail}>{detail}</span>)}
        </div>
      )}
    </div>
  );
};

// Two lines in the table; one "Created Oct 15 by …" line on cards.
export const Created = ({ date, by }) => (
  <div className="schedule-created">
    <div className="schedule-created__date">
      <DataList.CardOnly>Created </DataList.CardOnly>
      {shortDate(date)}
    </div>
    {by && (
      <div className="dl-muted dl-truncate schedule-created__by">
        <DataList.CardOnly>by </DataList.CardOnly>
        {by}
      </div>
    )}
  </div>
);

export const Person = ({ name, status, note }) => {
  const statusOption = statusFor(status);
  return (
    <DataList.Person
      name={name}
      dot={statusOption.swatch}
      secondary={`${statusOption.label}${note ? `, ${note}` : ''}`}
    />
  );
};

export const Unassigned = () => <DataList.Person empty={true} secondary="No technician yet" />;

const MAX_PEOPLE = 3;

export const Team = ({ assignments: all }) => {
  // Unassigned jobs come back with a placeholder assignment named
  // "Unassigned" and no technician; don't show that as a person.
  const assignments = (all || []).filter((a) => a.user_id || (a.name && a.name !== 'Unassigned'));
  if (assignments.length === 0) return <Unassigned />;
  const shown = assignments.length > MAX_PEOPLE ? assignments.slice(0, MAX_PEOPLE - 1) : assignments;
  const hidden = assignments.slice(shown.length);
  return (
    <div className="schedule-team">
      {shown.map((assignment) => (
        <Person key={assignment.id} name={assignment.name || assignment.technician_name} status={assignment.status} />
      ))}
      {hidden.length > 0 && (
        <div className="dl-muted schedule-team__more" title={hidden.map((a) => a.name).join(', ')}>
          +{hidden.length} more
        </div>
      )}
    </div>
  );
};

export const directionsUrl = (location) => {
  if (!location) return null;
  const destination = location.latitude && location.longitude
    ? `${location.latitude},${location.longitude}`
    : location.full_address;
  return destination
    ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`
    : null;
};

export const Directions = ({ location }) => {
  const url = directionsUrl(location);
  if (!url) return null;
  return (
    <a className="schedule-client__directions dl-raised" href={url} target="_blank" rel="noopener noreferrer">
      <Icon icon="path-search" iconSize={12} />
      Directions
    </a>
  );
};

export const Client = ({ name, link, location }) => {
  const address = location && location.full_address;
  return (
    <div className="schedule-client">
      {link ? (
        <a className="schedule-client__name dl-raised" href={link} title={name}>{name}</a>
      ) : (
        <span className="schedule-client__name" title={name}>{name}</span>
      )}
      {(address || directionsUrl(location)) && (
        <div className="schedule-client__where">
          {address && <span className="dl-muted schedule-client__address" title={address}>{address}</span>}
          <Directions location={location} />
        </div>
      )}
    </div>
  );
};

export const Billing = ({ invoiced }) => (
  <DataList.Pill tone={invoiced ? 'green' : 'grey'}>{invoiced ? 'Invoiced' : 'Not invoiced'}</DataList.Pill>
);

export const Priority = ({ priority }) => {
  const mapping = priorityMapping(priority);
  return (
    <span
      className="dl-pill schedule-priority"
      style={{ backgroundColor: mapping.background, color: mapping.text, borderColor: mapping.text }}
    >
      <span aria-hidden="true">{mapping.emoji}</span> {mapping.label}
    </span>
  );
};

export const EditJob = ({ onEdit }) => (
  <DataList.Actions items={[{ icon: 'edit', label: 'Edit job', onClick: onEdit }]} />
);

const JOB_FILTERS = {
  client_id: 'client',
  user_id: 'technicians',
  status: 'status',
  assignment_status: 'status',
  payment_status: 'payment status',
  service_type_id: 'service type',
  priority: 'priority',
  event_number: 'job number',
  po_number: 'PO number',
};

// Empty Jobs/Assignments list: says what narrows the results and offers to
// clear the filters or look further back.
export const Empty = ({ noun, search, onClearFilters, onWiden }) => {
  const where = (search && search.where) || {};
  const filters = DataList.activeFilters(where, JOB_FILTERS);
  const deleted = search && search.only_deleted;
  const start = where.start_date && where.start_date.gte;
  const end = where.end_date && where.end_date.lte;
  let range = null;
  if (start && end) {
    const from = currentTime(moment.unix(parseInt(start, 10)));
    const to = currentTime(moment.unix(parseInt(end, 10)));
    range = `${from.format(from.isSame(to, 'year') ? 'MMM D' : 'MMM D, YYYY')} and ${to.format('MMM D, YYYY')}`;
  }
  const what = `${filters.length > 0 ? 'matching ' : ''}${deleted ? 'deleted ' : ''}${noun}`;
  const list = (names) =>
    names.length <= 1 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;

  return (
    <DataList.Notice
      icon={filters.length > 0 ? 'filter-remove' : 'calendar'}
      title={range ? `No ${what} between ${range}` : `No ${what} found`}
      message={filters.length > 0 ? `Filtering by ${list(filters)}.` : 'Nothing was scheduled in this date range.'}
      actions={[
        (filters.length > 0 || deleted) && {
          label: 'Clear filters', icon: 'filter-remove', onClick: onClearFilters, primary: true,
        },
        start && { label: 'Include 3 more months', icon: 'history', onClick: onWiden },
      ]}
    />
  );
};

// "PO23545" stays as is; "4471" becomes "PO 4471".
export const poLabel = (po) => (po ? (/^po/i.test(String(po).trim()) ? String(po).trim() : `PO ${po}`) : null);

export const shortDate = (date) => (date ? dayLabel(currentTime(date)).replace(/^\w+, /, '') : '');
export const clockTime = (date) => (date ? currentTime(date).format('h:mm A') : '');

// Months covered by a search's date range (0 when it has no range).
const rangeMonths = (search) => {
  const where = (search && search.where) || {};
  const start = where.start_date && where.start_date.gte;
  const end = where.end_date && where.end_date.lte;
  if (!start || !end) return 0;
  return moment.unix(parseInt(end, 10)).diff(moment.unix(parseInt(start, 10)), 'months', true);
};

// Failed Jobs/Assignments load; offers a shorter range for long searches.
export const LoadError = ({ noun, search, onRetry, onNarrow }) => (
  <DataList.LoadError
    noun={noun}
    onRetry={onRetry}
    slowHint={rangeMonths(search) > 12 ? {
      message: 'The server didn\'t respond in time. Searching more than a year at once can be slow, so try a shorter date range.',
      label: 'Show the last 3 months',
      onClick: onNarrow,
    } : null}
  />
);

export const Loading = ({ variant = 'jobs', noun = 'jobs' }) => (
  variant === 'assignments' ? (
    <DataList.Loading columns={ASSIGNMENT_COLUMNS} cards={ASSIGNMENT_CARDS} minTableWidth={MIN_TABLE_WIDTH} noun={noun} />
  ) : (
    <DataList.Loading columns={jobColumns(false)} cards={jobCards(false)} minTableWidth={MIN_TABLE_WIDTH} noun={noun} />
  )
);
